/* eslint-disable no-console */
/* jshint esversion: 8 */
import React, { useState, useMemo, useEffect, useRef } from 'react';
import ChatBot, {
  ChatBotProvider,
  useMessages,
  useStyles,
  useSettings,
  usePaths,
  useChatWindow
} from 'react-chatbotify';
import './sofabuddy_chatbot/styles.sass';
import './translations/componentsInit';
import closeChatIcon from '/app/javascript/icons/svgs/minus-white.svg';
import { useTranslation } from 'react-i18next';
import {
  defaultStyles,
  chatbotSettings
} from './sofabuddy_chatbot/sofabuddyConfig';
import OpenAiWrapper from './OpenAiWrapper';
import { triggerSurvey } from './sofabuddy_chatbot/surveys';
import useChatInitialization from './sofabuddy_chatbot/customHooks/useChatInitialization';
import useChatFlowLogic from './sofabuddy_chatbot/customHooks/useChatFlowLogic';
import useTracking from './sofabuddy_chatbot/customHooks/useTracking';
import useChatHelpers from './sofabuddy_chatbot/customHooks/useChatHelpers';
import useChatEventListeners from './sofabuddy_chatbot/customHooks/useChatEventListeners';
import useStorageKey from './sofabuddy_chatbot/customHooks/useStorageKey';

const SofabuddyChatBotWrapper = ({
  sofabuddyCookieExists,
  sofabuddyThreadId,
  currentProfileId,
  dailyRemainingSofabuddyMessages,
  showSofabuddySurvey,
  sofabuddySurveyClicked
}) => {
  const { t } = useTranslation();
  const openAiKey = window.Sofatutor.Sofabuddy.openAi.apiKey;
  const baseURL = window.Sofatutor.Sofabuddy.openAi.baseURL;
  const { setMessages } = useMessages();
  const { setStyles } = useStyles(defaultStyles);
  const { setSettings } = useSettings();
  const { goToPath } = usePaths();
  const threadRef = useRef(null);
  const [messageSent, setMessageSent] = useState(false);
  const { isChatWindowOpen, toggleChatWindow } = useChatWindow();
  const { amplitudeTracking } = useTracking();

  const {
    usageCounter,
    setUsageCounter,
    initializeChat,
    resumeChat,
    disableChat
  } = useChatInitialization({
    dailyRemainingSofabuddyMessages,
    sofabuddyCookieExists,
    triggerSurvey,
    goToPath,
    setMessages,
    setSettings,
    setStyles,
    amplitudeTracking
  });

  const {
    cleanChatHistory,
    retrieveOrCreateThread,
    sendMessage
  } = useChatHelpers({
    threadRef,
    openAiWrapper: useMemo(() => {
      if (usageCounter <= 0) {
        return null;
      }
      return new OpenAiWrapper(openAiKey, baseURL);
    }, [openAiKey, baseURL, usageCounter]),
    usageCounter,
    setUsageCounter,
    disableChat,
    triggerSurvey,
    sofabuddyCookieExists
  });

  useChatEventListeners({
    isChatWindowOpen,
    toggleChatWindow,
    sofabuddyThreadId,
    amplitudeTracking,
    messageSent,
    setMessageSent,
    initializeChat
  });

  const flow = useChatFlowLogic({
    initializeChat,
    resumeChat,
    sendMessage,
    retrieveOrCreateThread,
    disableChat,
    threadRef,
    usageCounter,
    showSofabuddySurvey,
    currentProfileId,
    sofabuddyCookieExists,
    sofabuddySurveyClicked,
    t,
    amplitudeTracking
  });

  useEffect(() => {
    cleanChatHistory(currentProfileId, ['feedback', 'error', 'warning']);
  }, [currentProfileId, cleanChatHistory]);

  const storageKey = useStorageKey(currentProfileId);

  const settings = useMemo(
    () =>
      chatbotSettings(
        t,
        closeChatIcon,
        sofabuddyCookieExists,
        sofabuddyThreadId,
        storageKey
      ),
    [t, closeChatIcon, sofabuddyCookieExists, sofabuddyThreadId, storageKey]
  );

  return <ChatBot styles={defaultStyles} settings={settings} flow={flow} />;
};

const SofabuddyChatBot = ({
  sofabuddyCookieExists,
  sofabuddyThreadId,
  currentProfileId,
  dailyRemainingSofabuddyMessages,
  showSofabuddySurvey,
  sofabuddySurveyClicked
}) => {
  return (
    /* This is a Context provider from the ChatBot library that provides the necessary hooks to manipulate messages & styles arrays for the ChatBot without re-rendering */
    <ChatBotProvider>
      {/* This is the ChatBot Wrapper component that renders the main ChatBot library component and also contains logic for the ChatBot */}
      <SofabuddyChatBotWrapper
        sofabuddyCookieExists={sofabuddyCookieExists}
        sofabuddyThreadId={sofabuddyThreadId}
        currentProfileId={currentProfileId}
        dailyRemainingSofabuddyMessages={dailyRemainingSofabuddyMessages}
        showSofabuddySurvey={showSofabuddySurvey}
        sofabuddySurveyClicked={sofabuddySurveyClicked}
      />
    </ChatBotProvider>
  );
};

export default SofabuddyChatBot;
